// Panel with a chat.

import React from 'react';
import lodash from 'lodash'
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import { Drafty, Tinode } from '../tinode/tinode';

import CallPanel from '../widgets/call-panel.jsx';
import ChatMessage from '../widgets/chat-message.jsx';
import ContactBadges from '../widgets/contact-badges.jsx';
import DocPreview from '../widgets/doc-preview.jsx';
import ErrorPanel from '../widgets/error-panel.jsx';
import GroupSubs from '../widgets/group-subs.jsx';
import ImagePreview from '../widgets/image-preview.jsx';
import Invitation from '../widgets/invitation.jsx';
import LetterTile from '../widgets/letter-tile.jsx';
import LogoView from './logo-view.jsx';
import MetaMessage from '../widgets/meta-message.jsx';
import SendMessage from '../widgets/send-message.jsx';
import VideoPreview from '../widgets/video-preview.jsx';
import PhoneUtils from '../lib/phoneUtils'
import { Topic } from '../tinode/topic'

import {
  DEFAULT_P2P_ACCESS_MODE, EDIT_PREVIEW_LENGTH, IMAGE_PREVIEW_DIM, IMMEDIATE_P2P_SUBSCRIPTION,
  KEYPRESS_DELAY, MESSAGES_PAGE, MAX_EXTERN_ATTACHMENT_SIZE, MAX_IMAGE_DIM, MAX_INBAND_ATTACHMENT_SIZE, MAX_HIGH_IMAGE_DIM,
  READ_DELAY, QUOTED_REPLY_LENGTH, VIDEO_PREVIEW_DIM
} from '../config.js';
import { CALL_STATE_OUTGOING_INITATED, CALL_STATE_IN_PROGRESS } from '../constants.js';
import { blobToBase64, fileToBase64, imageScaled, makeImageUrl } from '../lib/blob-helpers.js';
import HashNavigation from '../lib/navigation.js';
import { bytesToHumanSize, relativeDateFormat, shortDateFormat } from '../lib/strformat.js';
import * as MC_CONST from '../tinode/constants'
import axios from 'axios';
import MessagesUtil from '../tinode/messagesUtils';
import { IconArrowDownward, IconMoreVert } from '../icons/svg';
import { profile } from "../stores/storages/ProfileStorage";
import Helpers from '../lib/helpers';
import Pin from '../widgets/pin.jsx';
import VoteNotification from '../widgets/vote-notification.jsx';
import YetAnotherLightbox from '../widgets/info-view/yet-image-lightbox.jsx';
import "yet-another-react-lightbox/styles.css"
import TimeHelpers from '../lib/time.js';
import SavedMessageServices from '../services/messages/saved-message';


// Run timer with this frequency (ms) for checking notification queue.
const NOTIFICATION_EXEC_INTERVAL = 300;
// Scroll distance before [go to latest message] button is shown.
const SHOW_GO_TO_LAST_DIST = 100;
// Sctoll distance from the top when fetching the page of earlier messages is triggered.
const FETCH_PAGE_TRIGGER = 40;
//config lastseen
export const MAX_JUST_NOW = 120000 // last seen less than 120 seconds => JUST NOW
export const MAX_MINUTES_AGO = 1800000 // last seen 2-30 mins => LAST SEEN ... MINUTES AGO

const messages = defineMessages({
  online_now: {
    id: 'online_now',
    defaultMessage: 'Online',
    description: 'Indicator that the user or topic is currently online',
  },
  last_seen: {
    id: 'last_seen_timestamp',
    defaultMessage: 'Last seen',
    description: 'Label for the timestamp of when the user or topic was last online'
  },
  not_found: {
    id: 'title_not_found',
    defaultMessage: 'Not found',
    description: 'Title shown when topic is not found'
  },
  channel: {
    id: 'channel',
    defaultMessage: 'channel',
    description: 'Subtitle shown for channels in MessagesView instead of last seen'
  },
  file_attachment_too_large: {
    id: 'file_attachment_too_large',
    defaultMessage: 'The file size {size} exceeds the {limit} limit.',
    description: 'Error message when attachment is too large'
  },
  file_attachment_not_support: {
    id: 'file_attachment_not_support',
    defaultMessage: 'The file have not supported.',
    description: 'Error message when attachment is not support'
  },
  upload_attachment_error_occured: {
    id: 'upload_attachment_error_occured',
    defaultMessage: 'An error occurred, please try uploading again.',
    description: 'Sometime file upload fails'
  },
  invalid_content: {
    id: 'invalid_content',
    defaultMessage: 'invalid content',
    description: 'Shown when the message is unreadable'
  },
  editing_message: {
    id: 'editing_message',
    defaultMessage: 'Editing',
    description: 'Title over message editing preview'
  },
  drag_file: {
    id: 'drag_file',
    defaultMessage: 'Drag file here',
    description: 'Prompt on the file drag-n-drop overlay banner'
  },
  member_count: {
    id: 'member_count',
    defaultMessage: '{size} members',
    description: 'Subtitle on Navbar chat'
  },
  internal_network_number: {
    id: 'internal_network_number',
    defaultMessage: 'Free SMS',
    description: 'Free SMS'
  },
  out_of_network_number: {
    id: 'out_of_network_number',
    defaultMessage: 'Sending a voice sticker is really fun!',
    description: 'Sending a voice sticker is really fun!'
  },
  not_in_group: {
    id: "not_in_group",
    defaultMessage: "You are not in this group",
    description: "You are not in this group"
  },
  officialAccount_label: {
    id: "officialAccount_label",
    defaultMessage: "Official account",
    description: "Official account"
  },
  lastOnline: {
    id: "lastOnline",
    defaultMessage: "Last seen {timeString}",
    description: "Last seen {timeString}"
  },
  justNow: {
    id: "justNow",
    defaultMessage: "Just now",
    description: "Just now"
  },
  minutesAgo: {
    id: "minutesAgo",
    defaultMessage: "{minutes} minutes ago",
    description: "{minutes} minutes ago"
  },
  lastSeenYesterday: {
    id: "lastSeenYesterday",
    defaultMessage: "Yesterday {HHmm}",
    description: "Yesterday {HHmm}"
  }
});

// Checks if the access permissions are granted but not yet accepted.
function isUnconfirmed(acs) {
  if (acs) {
    const ex = acs.getExcessive() || '';
    return acs.isJoiner('given') && (ex.includes('R') || ex.includes('W'));
  }
  return false;
}

function isPeerRestricted(acs) {
  if (acs) {
    const ms = acs.getMissing() || '';
    return acs.isJoiner('want') && (ms.includes('R') || ms.includes('W'));
  }
  return false;
}

function shouldPresentCallPanel(callState) {
  // Show call panel if either:
  // - call is outgoing (and the client is waiting for the other side to pick up) or,
  // - call is already in progress.
  return callState == CALL_STATE_OUTGOING_INITATED || callState == CALL_STATE_IN_PROGRESS;
}

class MessagesView extends React.Component {
  constructor(props) {
    super(props);

    this.state = MessagesView.getDerivedStateFromProps(props, {});

    this.componentSetup = this.componentSetup.bind(this);
    this.leave = this.leave.bind(this);
    this.sendMessage = this.sendMessage.bind(this);
    this.retrySend = this.retrySend.bind(this);
    this.sendImageAttachment = this.sendImageAttachment.bind(this);
    this.sendVideoAttachment = this.sendVideoAttachment.bind(this);
    this.sendFileAttachment = this.sendFileAttachment.bind(this);
    this.sendAudioAttachment = this.sendAudioAttachment.bind(this);
    this.sendKeyPress = this.sendKeyPress.bind(this);
    this.subscribe = this.subscribe.bind(this);
    this.handleScrollReference = this.handleScrollReference.bind(this);
    this.mountDnDEvents = this.mountDnDEvents.bind(this);
    this.handleScrollEvent = this.handleScrollEvent.bind(this);
    this.handleDescChange = this.handleDescChange.bind(this);
    this.handleSubsUpdated = this.handleSubsUpdated.bind(this);
    this.handleMessageUpdate = this.handleMessageUpdate.bind(this);
    this.handleAllMessagesReceived = this.handleAllMessagesReceived.bind(this);
    this.handleInfoReceipt = this.handleInfoReceipt.bind(this);
    this.handleExpandMedia = this.handleExpandMedia.bind(this);
    this.handleClosePreview = this.handleClosePreview.bind(this);
    this.handleFormResponse = this.handleFormResponse.bind(this);
    this.handleContextClick = this.handleContextClick.bind(this);
    this.handleShowMessageContextMenu = this.handleShowMessageContextMenu.bind(this);
    this.handleNewChatAcceptance = this.handleNewChatAcceptance.bind(this);
    this.handleEnablePeer = this.handleEnablePeer.bind(this);
    this.handleAttachFile = this.handleAttachFile.bind(this);
    this.handleAttachImageOrVideo = this.handleAttachImageOrVideo.bind(this);
    this.handleCancelUpload = this.handleCancelUpload.bind(this);
    this.postReadNotification = this.postReadNotification.bind(this);
    this.clearNotificationQueue = this.clearNotificationQueue.bind(this);
    this.goToLatestMessage = this.goToLatestMessage.bind(this);
    this.handleFileDrop = this.handleFileDrop.bind(this);

    this.handlePickReply = this.handlePickReply.bind(this);
    this.handleEditMessage = this.handleEditMessage.bind(this);
    this.handleSaveMessage = this.handleSaveMessage.bind(this);
    this.handleCancelReply = this.handleCancelReply.bind(this);
    this.handleQuoteClick = this.handleQuoteClick.bind(this);
    this.handleCallHangup = this.handleCallHangup.bind(this);

    this.isDragEnabled = this.isDragEnabled.bind(this);
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDragIn = this.handleDragIn.bind(this);
    this.handleDragOut = this.handleDragOut.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDrop = this.handleDrop.bind(this);

    props.tinode._chatMessageRefs = {};
    this.getOrCreateMessageRef = this.getOrCreateMessageRef.bind(this);
    this.setMessagePhoneNotInstallApp = this.setMessagePhoneNotInstallApp.bind(this);
    this.getLastSeen = this.getLastSeen.bind(this);

    // Keeps track of the drag event.
    // Need a counter b/c the browser's 'drag' events may fire multiple times
    // when the user takes the mouse pointer over the container:
    // for the component itself and for all nested/child elements.
    this.dragCounter = 0;
    this.dndRef = null;

    this.readNotificationQueue = [];
    this.readNotificationTimer = null;

    this.keyPressTimer = null;
  }

  getOrCreateMessageRef(seqId) {
    if (this.props.tinode._chatMessageRefs.hasOwnProperty(seqId)) {
      return this.props.tinode._chatMessageRefs[seqId];
    }
    const ref = React.createRef();
    this.props.tinode._chatMessageRefs[seqId] = ref;
    return ref;
  }

  componentDidMount() {
    if (this.messagesScroller) {
      this.messagesScroller.addEventListener('scroll', this.handleScrollEvent);
    }

    // Drag and drop events
    this.mountDnDEvents(this.dndRef);

    this.componentSetup({}, {});
  }

  componentWillUnmount() {
    if (this.messagesScroller) {
      this.messagesScroller.removeEventListener('scroll', this.handleScrollEvent);
    }

    // Flush all notifications.
    this.clearNotificationQueue();

    // Drag and drop events
    if (this.dndRef) {
      this.dndRef.removeEventListener('dragstart', this.handleDragStart);
      this.dndRef.removeEventListener('dragenter', this.handleDragIn);
      this.dndRef.removeEventListener('dragleave', this.handleDragOut);
      this.dndRef.removeEventListener('dragover', this.handleDrag);
      this.dndRef.removeEventListener('drop', this.handleDrop);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Scroll last message into view on component update e.g. on message received
    // or vertical shrinking.
    if (this.messagesScroller &&
      (prevState.topic != this.state.topic || prevState.maxSeqId != this.state.maxSeqId ||
        prevState.minSeqId != this.state.minSeqId)) {
      // New message.
      if (this.state.scrollPosition < SHOW_GO_TO_LAST_DIST) {
        this.messagesScroller.scrollTop = this.messagesScroller.scrollHeight -
          this.state.scrollPosition - this.messagesScroller.offsetHeight;
      }
    }

    if (!this.props.applicationVisible) {
      // If application is not visible, flush all unsent 'read' notifications.
      this.clearNotificationQueue();
    } else {
      // Otherwise assume there are unread messages.
      this.postReadNotification(0);
    }

    if (prevProps.topic !== this.props.topic) {
      const topic = this.props.tinode.getTopic(this.state.topic);
      topic && topic.read ? this.handleQuoteClick(topic.read, false) : null;
    }
    this.componentSetup(prevProps, prevState);
  }

  componentSetup(prevProps, prevState) {
    const topic = this.props.tinode ? this.props.tinode.getTopic(this.state.topic) : undefined;
    if (this.state.topic != prevState.topic) {
      if (prevState.topic && !Tinode.isNewGroupTopicName(prevState.topic)) {
        this.leave(prevState.topic);
        if (prevState.rtcPanel) {
          this.handleCallHangup(prevState.topic, prevProps.callSeq);
        }
      }

      if (topic) {
        topic.onData = this.handleMessageUpdate;
        topic.onAllMessagesReceived = this.handleAllMessagesReceived;
        topic.onInfo = this.handleInfoReceipt;
        topic.onMetaDesc = this.handleDescChange;
        topic.onSubsUpdated = this.handleSubsUpdated;
        topic.onPres = this.handleSubsUpdated;
      }
    }

    if (topic) {
      if ((this.state.topic != prevState.topic) || (this.props.myUserId && !prevProps.myUserId)) {
        // Don't immediately subscribe to a new p2p topic, wait for the first message.
        const newTopic = (this.props.newTopicParams && this.props.newTopicParams._topicName == this.props.topic);
        if (topic.isP2PType() && newTopic && !IMMEDIATE_P2P_SUBSCRIPTION) {
          topic.getMeta(topic.startMetaQuery().withDesc().build());
        } else if (this.props.myUserId) {
          this.subscribe(topic);
        }
      } else if (topic.isSubscribed() && this.state.isReader && !prevState.isReader) {
        // If reader status has changed and data became available.
        topic.getMeta(topic.startMetaQuery().withLaterData().build());
      }
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    let nextState = {};
    if (!nextProps.topic) {
      // Default state: no topic.
      nextState = {
        minSeqId: -1,
        maxSeqId: -1,
        latestClearId: -1,
        onlineSubs: [],
        groupMembers: [],
        topic: null,
        title: '',
        avatar: null,
        isVerified: false,
        isStaff: false,
        isDangerous: false,
        deleted: false,
        docPreview: null,
        imagePreview: null,
        imagePostview: null,
        videoPreview: null,
        videoPostview: null,
        rtcPanel: null,
        typingIndicator: false,
        scrollPosition: 0,
        fetchingMessages: false,
        peerMessagingDisabled: false,
        channel: false,
        reply: null,
        contentToEdit: null,
        showGoToLastButton: false,
        dragging: false,
        subsVersion: 0,
        showPopup: false,
        indexImage: 0,
        listImagesShow: [],
        loaderMessage: false
      };
    } else if (nextProps.topic != prevState.topic) {
      const topic = nextProps.tinode.getTopic(nextProps.topic);

      nextState = {
        topic: nextProps.topic,
        deleted: topic._deleted,
        docPreview: null,
        imagePreview: null,
        imagePostview: null,
        videoPreview: null,
        videoPostview: null,
        rtcPanel: null,
        typingIndicator: false,
        scrollPosition: 0,
        fetchingMessages: false,
        showGoToLastButton: false,
        contentToEdit: null,
        dragging: false
      };

      nextState.reply = null;

      if (topic) {
        // Topic exists.
        const subs = [];
        const groupMembers = []

        if (nextProps.connected) {
          topic.subscribers((sub) => {
            if (sub.online && sub.user != nextProps.myUserId) {
              subs.push(sub);
            }
            groupMembers.push(sub)
          });
        }

        Object.assign(nextState, {
          onlineSubs: subs,
          groupMembers,
        });

        if (topic.public) {
          const newTitle = topic.private && topic.private.comment ? topic.private.comment : topic.public.fn
          if (prevState.title !== '' && newTitle !== prevState.title) {
            // debugger
          }
          Object.assign(nextState, {
            title: topic.private && topic.private.comment ? topic.private.comment : topic.public.fn,
            avatar: makeImageUrl(topic.public.photo)
          });
        } else {
          Object.assign(nextState, {
            title: '',
            avatar: null
          });
        }

        const peer = topic.p2pPeerDesc();
        if (peer) {
          Object.assign(nextState, {
            peerMessagingDisabled: isPeerRestricted(peer.acs)
          });
        } else if (prevState.peerMessagingDisabled) {
          Object.assign(nextState, {
            peerMessagingDisabled: false
          });
        }
        Object.assign(nextState, {
          minSeqId: topic.minMsgSeq(),
          maxSeqId: topic.maxMsgSeq(),
          latestClearId: topic.maxClearId(),
          channel: topic.isChannelType()
        });

        if (nextProps.callTopic == topic.name && shouldPresentCallPanel(nextProps.callState)) {
          nextState.rtcPanel = nextProps.callTopic;
        }
      } else {
        // Invalid topic.
        Object.assign(nextState, {
          minSeqId: -1,
          maxSeqId: -1,
          latestClearId: -1,
          onlineSubs: [],
          groupMembers: [],
          title: '',
          avatar: null,
          peerMessagingDisabled: false,
          channel: false
        });
      }
    } else {
      // We are still in same topic. Show the call panel if necessary.
      if (nextProps.callTopic == prevState.topic && !prevState.rtcPanel &&
        shouldPresentCallPanel(nextProps.callState)) {
        nextState.rtcPanel = nextProps.callTopic;
      }
    }

    if (nextProps.acs) {
      if (nextProps.acs.isWriter() != prevState.isWriter) {
        nextState.isWriter = !prevState.isWriter;
      }
      if (nextProps.acs.isReader() != prevState.isReader) {
        nextState.isReader = !prevState.isReader;
      }
      if (!nextProps.acs.isReader('given') != prevState.readingBlocked) {
        nextState.readingBlocked = !prevState.readingBlocked;
      }
      if (nextProps.acs.isSharer() != prevState.isSharer) {
        nextState.isSharer = !prevState.isSharer;
      }
    } else {
      if (prevState.isWriter) {
        nextState.isWriter = false;
      }
      if (prevState.isReader) {
        nextState.isReader = false;
      }
      if (!prevState.readingBlocked) {
        prevState.readingBlocked = true;
      }
      if (prevState.isSharer) {
        nextState.isSharer = false;
      }
    }

    if (isUnconfirmed(nextProps.acs) == !prevState.unconformed) {
      nextState.unconfirmed = !prevState.unconformed;
    }

    // Clear subscribers online when there is no connection.
    if (!nextProps.connected && prevState.onlineSubs && prevState.onlineSubs.length > 0) {
      nextState.onlineSubs = [];
    }

    return nextState;
  }

  subscribe(topic) {
    if (topic.isSubscribed() || !this.props.ready) {
      return;
    }

    // Is this a new topic?
    const newTopic = (this.props.newTopicParams && this.props.newTopicParams._topicName == this.props.topic);
    // Don't request the tags. They are useless unless the user
    // is the owner and is editing the topic.
    let getQuery = topic.startMetaQuery().withLaterDesc().withLaterSub();
    if (this.state.isReader || newTopic) {
      // Reading is either permitted or we don't know because it's a new topic. Ask for messages.
      getQuery = getQuery.withLaterData(MESSAGES_PAGE);
      if (this.state.isReader) {
        getQuery = getQuery.withLaterDel();
      }
      // And show "loading" spinner.
      // this.setState({ fetchingMessages: true });
    }

    const setQuery = newTopic ? this.props.newTopicParams : undefined;
    topic.subscribe(getQuery.build(), setQuery)
      .then(ctrl => {
        if (ctrl.code == 303) {
          // Redirect to another topic requested.
          HashNavigation.navigateTo(HashNavigation.setUrlTopic('', ctrl.params.topic));
          return;
        }
        if (this.state.topic != ctrl.topic) {
          this.setState({ topic: ctrl.topic });
        }
        if (this.state.deleted) {
          this.setState({ deleted: false });
        }
        this.props.onNewTopicCreated(this.props.topic, ctrl.topic);
        // If there are unsent messages (except hard-failed and video call messages),
        // try sending them now. Hard-failed and unsent video call messages will be dropped.
        let discard = [];
        topic.queuedMessages(pub => {
          if (pub._sending) {
            return;
          }
          if (pub._fatal || (pub.head && pub.head.webrtc)) {
            // Filter out unsent failed & video call messages.
            discard.push(pub.seq);
            return;
          }
          if (topic.isSubscribed()) {
            this.retrySend(pub);
          }
        });
        if (discard.length > 0) {
          topic.delMessagesList(discard, true);
        }
      })
      .catch(err => {
        console.error("Failed subscription to", this.state.topic, err);
        this.props.onError(err.message, 'err');
        const blankState = MessagesView.getDerivedStateFromProps({}, {});
        blankState.title = this.props.intl.formatMessage(messages.not_found);
        this.setState(blankState);
      });
  }

  leave(oldTopicName) {
    if (!oldTopicName || !this.props.tinode.isTopicCached(oldTopicName)) {
      return;
    }

    const oldTopic = this.props.tinode.getTopic(oldTopicName);
    if (oldTopic) {
      oldTopic.leave(false)
        .catch(_ => { /* do nothing here */ })
        .finally(_ => {
          // We don't care if the request succeeded or failed.
          // The topic is dead regardless.
          this.setState({ fetchingMessages: false });
          oldTopic.onData = undefined;
          oldTopic.onAllMessagesReceived = undefined;
          oldTopic.onInfo = undefined;
          oldTopic.onMetaDesc = undefined;
          oldTopic.onSubsUpdated = undefined;
          oldTopic.onPres = undefined;
        });
    }
  }

  // Don't use React.createRef as the ref.current is not available in componentDidMount in this component.
  handleScrollReference(node) {
    if (node) {
      node.addEventListener('scroll', this.handleScrollEvent);
      this.messagesScroller = node;
      this.messagesScroller.scrollTop = this.messagesScroller.scrollHeight -
        this.state.scrollPosition - this.messagesScroller.offsetHeight;
    }
  }


  // Get older messages and show/hide [go to latest message] button.
  handleScrollEvent(event) {
    const pos = event.target.scrollHeight - event.target.scrollTop - event.target.offsetHeight;
    this.setState({
      scrollPosition: pos,
      // Show [go to latest message] if far enough from bottom and scrolling down.
      showGoToLastButton: (pos > SHOW_GO_TO_LAST_DIST) && (pos < this.state.scrollPosition),
    });

    if (this.state.fetchingMessages) {
      return;
    }

    if (event.target.scrollTop <= FETCH_PAGE_TRIGGER) {
      const topic = this.props.tinode.getTopic(this.state.topic);
      if (topic && topic.isSubscribed() && topic.msgHasMoreMessages()) {
        this.setState({ fetchingMessages: true }, _ => {
          this.setState({ loaderMessage: true })
          topic.getMessagesPage(MESSAGES_PAGE)
            .then(res => {
              this.setState({ loaderMessage: false })
            })
            .catch(err => { console.log(err); this.props.onError(err.message, 'err') })
            .finally(_ => {
              this.setState({ fetchingMessages: false });
              this.messagesScroller.scrollTop = this.messagesScroller.scrollHeight -
                pos - this.messagesScroller.offsetHeight;
            });
        });
      }
    }
  }

  /* Mount drag and drop events */
  mountDnDEvents(dnd) {
    if (dnd) {
      dnd.addEventListener('dragstart', this.handleDragStart);
      dnd.addEventListener('dragenter', this.handleDragIn);
      dnd.addEventListener('dragleave', this.handleDragOut);
      dnd.addEventListener('dragover', this.handleDrag);
      dnd.addEventListener('drop', this.handleDrop);
      this.dndRef = dnd;
    }
  }

  goToLatestMessage() {
    this.setState({ scrollPosition: 0 });
    if (this.messagesScroller) {
      this.messagesScroller.scrollTop = this.messagesScroller.scrollHeight - this.messagesScroller.offsetHeight;
    }
  }

  handleDescChange(desc) {
    if (desc.public) {
      const newTitle = desc.private && desc.private.comment ? desc.private.comment : desc.public.fn
      if (this.state.title !== '' && newTitle !== this.state.title) {
        // debugger
      }
      this.setState({
        title: desc.private && desc.private.comment ? desc.private.comment : desc.public.fn,
        avatar: makeImageUrl(desc.public.photo)
      });
    } else {
      this.setState({
        title: '',
        avatar: null
      });
    }

    if (desc.acs) {
      this.setState({
        isWriter: desc.acs.isWriter(),
        isReader: desc.acs.isReader(),
        readingBlocked: !desc.acs.isReader('given'),
        unconfirmed: isUnconfirmed(desc.acs),
      });
    }
  }

  postReadNotification(seq) {
    // Ignore notifications if the app is invisible.
    if (!this.props.applicationVisible) {
      return;
    }

    // Set up the timer if it's not running already.
    if (!this.readNotificationTimer) {
      this.readNotificationTimer = setInterval(_ => {
        if (this.readNotificationQueue.length == 0) {
          // Shut down the timer if the queue is empty.
          clearInterval(this.readNotificationTimer);
          this.readNotificationTimer = null;
          return;
        }

        let seq = -1;
        while (this.readNotificationQueue.length > 0) {
          const n = this.readNotificationQueue[0];
          if (n.topicName != this.state.topic) {
            // Topic has changed. Drop the notification.
            this.readNotificationQueue.shift();
            continue;
          }

          const now = new Date();
          if (n.sendAt <= now) {
            // Remove expired notification from queue.
            this.readNotificationQueue.shift();
            seq = Math.max(seq, n.seq);
          } else {
            break;
          }
        }

        // Send only one notification for the whole batch of messages.
        if (seq >= 0) {
          const topic = this.props.tinode.getTopic(this.state.topic);
          if (topic) {
            topic.noteRead(seq);
          }
        }
      }, NOTIFICATION_EXEC_INTERVAL);
    }

    const now = new Date();
    this.readNotificationQueue.push({
      topicName: this.state.topic,
      seq: seq,
      sendAt: now.setMilliseconds(now.getMilliseconds() + READ_DELAY)
    });
  }

  // Clear notification queue and timer.
  clearNotificationQueue() {
    this.readNotificationQueue = [];
    if (this.readNotificationTimer) {
      clearInterval(this.readNotificationTimer);
      this.readNotificationTimer = null;
    }
  }

  handleSubsUpdated() {
    if (this.state.topic) {
      const subs = [];
      const groupMembers = [];
      const topic = this.props.tinode.getTopic(this.state.topic);
      topic.subscribers(sub => {
        if (sub.online && sub.user != this.props.myUserId) {
          subs.push(sub);
        }
        groupMembers.push(sub)
      });
      const newState = { onlineSubs: subs, groupMembers, subsVersion: this.state.subsVersion + 1 };
      const peer = topic.p2pPeerDesc();
      if (peer) {
        Object.assign(newState, {
          peerMessagingDisabled: isPeerRestricted(peer.acs)
        });
      } else if (this.state.peerMessagingDisabled) {
        Object.assign(newState, {
          peerMessagingDisabled: false
        });
      }
      this.setState(newState);
    }
  }

  // The 'msg' could be false-ish if some message ranges were deleted.
  handleMessageUpdate(msg) {
    if (!this.state.topic) {
      return;
    }
    const topic = this.props.tinode.getTopic(this.state.topic);
    if (!msg) {
      // msg could be null if one or more messages were deleted.
      // Updating state to force redraw.
      this.setState({ latestClearId: topic.maxClearId() });
      return;
    }

    clearTimeout(this.keyPressTimer)
    this.setState({ maxSeqId: topic.maxMsgSeq(), minSeqId: topic.minMsgSeq(), typingIndicator: false }, _ => {
      // Scroll to the bottom if the message is added to the end of the message
      // list if already at the bottom, otherwise show [go to latest] button.
      // Implemented as a callback to be sure the scroll height has been updated.
      if (topic.isNewMessage(msg.seq)) {
        if (this.state.scrollPosition > SHOW_GO_TO_LAST_DIST) {
          this.setState({ showGoToLastButton: true });
        } else {
          this.goToLatestMessage();
        }
      } else {
        if (this.messagesScroller) {
          this.messagesScroller.scrollTop = this.messagesScroller.scrollHeight - this.state.scrollPosition -
            this.messagesScroller.offsetHeight;
        }
      }
    });

    // Aknowledge messages except own messages. They are
    // automatically assumed to be read and recived.
    const status = topic.msgStatus(msg, true);
    if (status >= Tinode.MESSAGE_STATUS_SENT && msg.from != this.props.myUserId) {
      this.postReadNotification(msg.seq);
    }
  }

  handleAllMessagesReceived(count) {
    this.setState({ fetchingMessages: false });
    if (count > 0) {
      // 0 means "latest".
      this.postReadNotification(0);
    }
  }

  handleInfoReceipt(info) {
    switch (info.what) {
      case 'kp': {
        clearTimeout(this.keyPressTimer);
        this.keyPressTimer = setTimeout(_ => this.setState({ typingIndicator: false }), KEYPRESS_DELAY + 1000);
        if (!this.state.typingIndicator) {
          this.setState({ typingIndicator: true });
        }
        break;
      }
      case 'read':
      case 'recv':
        // Redraw due to changed recv/read status.
        this.forceUpdate();
        break;
      default:
        console.info("Other change in topic: ", info.what);
    }
  }

  handleExpandMedia(content) {
    if (!content) {
      return;
    }

    if (content.video) {
      this.setState({ videoPostview: content });
    } else {
      const topic = this.props.tinode.getTopic(this.state.topic);
      let listImages = topic?.shareImage ? topic.shareImage.reverse() : [];
      let isSrcContain = false;
      let indexImageInList = 0
      const srcImage = content.url ? content.url : undefined
      let listImageOne = [];
      if (listImages && listImages.length > 0 && content?.msgInfo?.id) {
        for (let i = 0; i < listImages.length; i++) {
          if (listImages[i].messageId === content.msgInfo.id) {
            isSrcContain = true
            indexImageInList = i
            break;
          }
        }
        if (isSrcContain) {
          this.setState({ showPopup: true, listImagesShow: listImages, indexImage: indexImageInList });
        } else {
          if (srcImage) {
            const imgInfo = {
              media_link: srcImage,
              messageId: content.msgInfo.id
            }
            listImageOne = [imgInfo];
            this.setState({ showPopup: true, listImagesShow: listImageOne, indexImage: 0 });
          } else {
            this.setState({ imagePostview: content });
          }
        }
      } else {
        if (srcImage) {
          const imgInfo = {
            media_link: srcImage
          }
          listImageOne = [imgInfo];
          this.setState({ showPopup: true, listImagesShow: listImageOne, indexImage: 0 });
        } else {
          this.setState({ imagePostview: content });
        }
      }
    }
  }

  handleClosePreview() {
    if (this.state.imagePreview && this.state.imagePreview.url) {
      URL.revokeObjectURL(this.state.imagePreview.url);
    }
    if (this.state.videoPreview && this.state.videoPreview.url) {
      URL.revokeObjectURL(this.state.videoPreview.url);
    }
    this.setState({ imagePostview: null, imagePreview: null, docPreview: null, videoPreview: null, videoPostview: null });
  }

  handleFormResponse(action, text, data) {
    if (action == 'pub') {
      this.sendMessage(Drafty.attachJSON(Drafty.parse(text), data));
    } else if (action == 'url') {
      const url = new URL(data.ref);
      const params = url.searchParams;
      for (let key in data.resp) {
        if (data.resp.hasOwnProperty(key)) {
          params.set(key, data.resp[key]);
        }
      }
      ['name', 'seq'].map((key) => {
        if (data[key]) {
          params.set(key, data[key]);
        }
      });
      params.set('uid', this.props.myUserId);
      params.set('topic', this.state.topic);
      url.search = params;
      window.open(url, '_blank');
    } else {
      console.info("Unknown action in form", action);
    }
  }

  handleContextClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if ((this.state.isWriter && Topic.isGroupTopicName(this.state.topic)) || !Topic.isGroupTopicName(this.state.topic)) {
      this.props.handleShowInfoView()
    } else {
      Helpers.showErrorToasts(this.props.intl.formatMessage(messages.not_in_group))
    }
  }

  handleShowMessageContextMenu(params, messageSpecificMenuItems) {
    if (params.userFrom == 'chan') {
      params.userFrom = this.state.topic;
      params.userName = this.state.title;
    }
    params.topicName = this.state.topic;
    const menuItems = messageSpecificMenuItems || [];
    const topic = this.props.tinode.getTopic(params.topicName);
    if (topic) {
      if (!topic.isChannelType()) {
        menuItems.push('message_delete');
      }
      const acs = topic.getAccessMode();
      const fromMsg = lodash.has(params, 'msg.from') ? params.msg.from : ''
      if (!topic.isOfficial && acs && acs.isDeleter() && fromMsg === this.props.myUserId) {
        menuItems.push('message_delete_hard');
      }
    }
    this.props.showContextMenu(params, menuItems);
  }

  handleNewChatAcceptance(action) {
    this.props.onNewChat(this.state.topic, action);
  }

  handleEnablePeer(e) {
    e.preventDefault();
    this.props.onChangePermissions(this.state.topic, DEFAULT_P2P_ACCESS_MODE, this.state.topic);
  }

  sendKeyPress(audio) {
    const topic = this.props.tinode.getTopic(this.state.topic);
    if (topic.isSubscribed()) {
      if (audio) {
        topic.noteRecording(true);
      } else {
        topic.noteKeyPress();
      }
    }
  }

  // sendMessage sends the message with an optional subscription to topic first.
  sendMessage(msg, uploadCompletionPromise, uploader) {
    let head;
    if (this.state.reply && (typeof msg === 'string' || msg.subtype === MC_CONST.NODE_SUBTYPE_MESSAGE.TEXT)) {
      if (this.state.reply.editing) {
        if (msg == this.state.contentToEdit) {
          // Message unchanged.
          this.handleCancelReply();
          return;
        }
        // Editing an existing message.
        head = {
          replace: ':' + this.state.reply.seq
        };
      } else if (this.state.reply.content) {
        const content = msg
        // Replying to a message in this topic.
        // Turn it into Drafty so we can make a quoted Drafty object later.
        head = {
          mime: Drafty.getContentType(),
          reply: '' + this.state.reply.seq
        };
        let msgContent = msg
        if (typeof msg == 'string') {
          msg = Drafty.parse(msg);
        } else {
          msgContent = msg.content
          delete content.content
        }
        msg = Drafty.append(Drafty.sanitizeEntities(this.state.reply.content), msgContent);
        msg.replyBody = msgContent
        msg = Object.assign({}, content, msg)
        const topic = this.props.tinode.getTopic(this.state.topic);
        const replyMsg = topic.findMessage(this.state.reply.seq)

        if (replyMsg && replyMsg.subtype) {
          let replyingMessage = null

          replyingMessage = {
            subtype: replyMsg.subtype,
            member: Topic.getThreadId(replyMsg.from),
            msgId: replyMsg.id,
          }

          if (replyMsg && replyMsg.tags) {
            replyingMessage.bodyTag = replyMsg.tags
            replyingMessage.bodyTagAll = replyMsg.tagAll
          }

          switch (replyMsg.subtype) {
            case MC_CONST.NODE_SUBTYPE_MESSAGE.TEXT: {
              let body = replyMsg.content
              if (typeof replyMsg.content !== 'string') {
                body = replyMsg.content.replyBody || replyMsg.content.content
              }
              replyingMessage.body = body
              break;
            }
            case MC_CONST.NODE_SUBTYPE_MESSAGE.IMAGE: {
              replyingMessage.link = replyMsg.content.mediaLink
              break;
            }
            case MC_CONST.NODE_SUBTYPE_MESSAGE.FILE: {
              replyingMessage.body = replyMsg.content.fname
              replyingMessage.link = parseInt(replyMsg.content.size)
              replyingMessage.media_link = replyMsg.content.mediaLink
              break;
            }
            case MC_CONST.NODE_SUBTYPE_MESSAGE.VIDEO: {
              replyingMessage.body = ''
              replyingMessage.link = replyMsg.content.thumb
              replyingMessage.linkFile = replyMsg.content.mediaLink
              break;
            }
          }

          msg.replyMsg = replyingMessage
        }
      }
      this.handleCancelReply();
    }
    const subtype = msg ? msg.subtype : ''
    switch (subtype) {
      case MC_CONST.NODE_SUBTYPE_MESSAGE.VOICE_STICKER:
      case MC_CONST.NODE_SUBTYPE_MESSAGE.CONTACT: {
        head = {
          mime: Drafty.getContentType(),
        }
        break
      }
    }

    this.props.sendMessage(msg, uploadCompletionPromise, uploader, head);
    this.goToLatestMessage();
  }

  sendMessageAttach(msg, uploadCompletionPromise, uploader, isReply = true) {
    let head = {
      mime: Drafty.getContentType(),
    };
    if (this.props.forwardMessage) {
      // We are forwarding a message.
      msg = this.props.forwardMessage.msg;
      head = this.props.forwardMessage.head;
      this.handleCancelReply();
    } else if (this.state.reply && isReply) {
      if (this.state.reply.editing) {
        if (msg == this.state.contentToEdit) {
          // Message unchanged.
          this.handleCancelReply();
          return;
        }
        // Editing an existing message.
        head = { replace: ':' + this.state.reply.seq };
      } else if (this.state.reply.content) {
        // Replying to a message in this topic.
        // Turn it into Drafty so we can make a quoted Drafty object later.
        head = {
          reply: '' + this.state.reply.seq
        };
        if (typeof msg == 'string') {
          msg = Drafty.parse(msg);
        }
        msg = Drafty.append(Drafty.sanitizeEntities(this.state.reply.content), msg);
      }
      this.handleCancelReply();
    }
    this.goToLatestMessage()
    return this.props.sendMessage(msg, uploadCompletionPromise, uploader, head);
  }

  // Retry sending a message.
  retrySend(pub) {
    this.props.sendMessage(pub.content, undefined, undefined, pub.head)
      .then(_ => {
        // All good. Remove the original message draft from the cache.
        const topic = this.props.tinode.getTopic(this.state.topic);
        topic.delMessagesList([pub.seq], true);
      });
  }

  // Send attachment as Drafty message:
  // - if file is too large, upload it and send a s link.
  // - if file is small enough, just send it in-band.
  sendFileAttachment(file) {
    // Server-provided limit reduced for base64 encoding and overhead.
    const maxInbandAttachmentSize = (this.props.tinode.getServerParam('maxMessageSize',
      MAX_INBAND_ATTACHMENT_SIZE) * 0.75 - 1024) | 0;

    // Too large to send inband - uploading out of band and sending as a link.
    const uploader = this.props.tinode.getLargeFileHelper();
    if (!uploader) {
      this.props.onError(this.props.intl.formatMessage(messages.cannot_initiate_upload));
      return;
    }

    const fname = file.name
    const size = file.size

    const uploadCompletionPromise = uploader.upload(file).then((res) => {
      if (lodash.has(res, 'errorCode')) {
        if (res.errorCode !== 0) {
          Helpers.showErrorToasts(this.props.intl.formatMessage(messages.file_attachment_not_support, {}), 'err');
          throw new Error(this.props.intl.formatMessage(messages.file_attachment_not_support, {}))
          // return {
          //   err: this.props.intl.formatMessage(messages.file_attachment_not_support, {})
          // }
        } else {

          if(!res.link) {
            Helpers.showErrorToasts(this.props.intl.formatMessage(messages.upload_attachment_error_occured, {}), 'err');
            throw new Error(this.props.intl.formatMessage(messages.upload_attachment_error_occured, {}));
          }
          // Pass data and the uploader to the TinodeWeb.
          return (res.link)
        }
      }
    })

    const msg = Drafty.attachFile(null, {
      mime: file.type,
      filename: fname,
      size: size,
      urlPromise: uploadCompletionPromise,
    });

    msg.subtype = MC_CONST.NODE_SUBTYPE_MESSAGE.FILE
    msg.fname = fname
    msg.size = size

    this.sendMessageAttach(msg, uploadCompletionPromise, uploader, false);

  }

  // handleAttachFile method is called when [Attach file] button is clicked: launch attachment preview.
  handleAttachFile(listFile) {
    listFile.forEach((file) => {
      const maxExternAttachmentSize = this.props.tinode.getServerParam('maxFileUploadSize', MAX_EXTERN_ATTACHMENT_SIZE);

      if (file.size > maxExternAttachmentSize) {
        // Too large.
        Helpers.showErrorToasts(this.props.intl.formatMessage(messages.file_attachment_too_large,
          { size: bytesToHumanSize(file.size), limit: bytesToHumanSize(maxExternAttachmentSize) }))
      } else {
        this.sendFileAttachment(file)
      }
    })
  }

  handleCallHangup(topic, seq) {
    this.props.onVideoCallClosed();
    this.setState({
      rtcPanel: null
    });
    this.props.onCallHangup(topic, seq);
  }

  // sendImageAttachment sends the image bits as Drafty message.
  sendImageAttachment(caption, blob, msgData = this.state.imagePreview || {}) {
    try {
      const mime = msgData.mime;
      const width = msgData.width;
      const height = msgData.height;
      const ratio = width / height;
      const fname = msgData.filename;

      // Server-provided limit reduced for base64 encoding and overhead.
      const maxInbandAttachmentSize = 0;

      if (blob.size > maxInbandAttachmentSize) {
        // Too large to send inband - uploading out of band and sending as a link.
        const uploader = this.props.tinode.getLargeFileHelper();
        if (!uploader) {
          this.props.onError(this.props.intl.formatMessage(messages.cannot_initiate_upload));
          return;
        }

        const uploadCompletionPromise = uploader.upload(blob).then((res) => {
          if (lodash.has(res, 'errorCode')) {
            if (res.errorCode !== 0) {
              this.props.onError(this.props.intl.formatMessage(messages.file_attachment_not_support, {}), 'err');
              throw new Error(this.props.intl.formatMessage(messages.file_attachment_not_support, {}))
              // return {
              //   err: this.props.intl.formatMessage(messages.file_attachment_not_support, {})
              // }
            } else {
              if(!res.link) {
                Helpers.showErrorToasts(this.props.intl.formatMessage(messages.upload_attachment_error_occured, {}), 'err');
                throw new Error(this.props.intl.formatMessage(messages.upload_attachment_error_occured, {}))
              }
              return res.link
            }
          }
        });

        const imageMsg = Drafty.insertImage(null, 0, {
          mime,
          width: width,
          height: height,
          filename: fname,
          size: blob.size,
          urlPromise: uploadCompletionPromise,
        })

        imageMsg.subtype = MC_CONST.NODE_SUBTYPE_MESSAGE.IMAGE
        imageMsg.fname = fname
        imageMsg.ratio = ratio



        this.sendMessageAttach(imageMsg, uploadCompletionPromise, uploader, false)
        if (!!caption && caption !== '') {
          this.sendMessage(caption, null, null);
        }

        return;
      }
    } catch (e) {
      console.error(e);
    }
  }

  // sendVideoAttachment sends the video bits as Drafty message.
  sendVideoAttachment(caption, videoBlob, previewBlob, params) {
    const width = params.width;
    const height = params.height;

    // Server-provided limit reduced for base64 encoding and overhead.
    const maxInbandAttachmentSize = 0;

    const uploads = [];
    let uploader;
    if (videoBlob.size > maxInbandAttachmentSize) {
      // One or both are too large to send inband. Uploading out of band and sending as a link.
      uploader = this.props.tinode.getLargeFileHelper();
      if (!uploader) {
        this.props.onError(this.props.intl.formatMessage(messages.cannot_initiate_upload));
        return;
      }

      uploader.upload(videoBlob).then((res) => {
        if (lodash.has(res, 'errorCode')) {
          if (res.errorCode !== 0) {
            this.props.onError(this.props.intl.formatMessage(messages.file_attachment_not_support, {}), 'err');
            throw new Error(this.props.intl.formatMessage(messages.file_attachment_not_support, {}))
            // return {
            //   err: this.props.intl.formatMessage(messages.file_attachment_not_support, {})
            // }
          } else {
            if(!res.link) {
              Helpers.showErrorToasts(this.props.intl.formatMessage(messages.upload_attachment_error_occured, {}), 'err')
              throw new Error(this.props.intl.formatMessage(messages.file_attachment_not_support, {}))
            }
            const fname = params.name
            const thumb = Helpers.formatURL(res.thumb)
            const size = videoBlob.size
            const duration = params.duration
            const mediaLink = res.link

            let i64 = null

            axios({
              url: thumb,
              method: 'GET',
              responseType: 'blob',
            }).then((response) => {
              const imageBlob = response.data
              if (imageBlob) {
                return blobToBase64(imageBlob);
              }
              return new Promise((resolve, reject) => { resolve(null) })
            }).then((res) => {
              i64 = res
            }).catch((e) => {
              console.error(e)
            }).finally(() => {
              const videoData = {
                mime: params.mime,
                width: width,
                height: height,
                duration: duration,
                filename: fname,
                size: size,
                refurl: mediaLink,
                preref: thumb,
              }
              if (i64) {
                videoData.premime = i64.mime
                videoData.preview = i64.bits
              }

              const videoMsg = Drafty.insertVideo(null, 0, videoData);

              videoMsg.subtype = MC_CONST.NODE_SUBTYPE_MESSAGE.VIDEO
              videoMsg.uploadRes = res
              videoMsg.fname = fname
              videoMsg.mediaLink = res.link
              videoMsg.mediaId = res.code
              videoMsg.thumb = thumb
              videoMsg.size = size
              videoMsg.duration = duration
              this.sendMessageAttach(videoMsg)
              if (!!caption && caption !== '') {
                this.sendMessage(caption, null, null);
              }
            })

          }
        }
      });
    }
  }

  // handleAttachImageOrVideo method is called when [Attach image or video] button is clicked: launch image or video preview.
  handleAttachImageOrVideo(listFile) {
    listFile.forEach((file) => {
      const maxExternAttachmentSize = this.props.tinode.getServerParam('maxFileUploadSize', MAX_EXTERN_ATTACHMENT_SIZE);
      let maxImageDim = MAX_IMAGE_DIM
      if (profile.isHighPhotoQuality) {
        maxImageDim = MAX_HIGH_IMAGE_DIM
      }

      if (file.type.startsWith('video/')) {
        const v = document.createElement('video');
        v.src = URL.createObjectURL(file)
        v.addEventListener("loadedmetadata", e => {
          const params = {
            width: e.target.videoWidth,
            height: e.target.videoHeight,
            duration: e.target.duration * 1000 | 0,
            mime: file.type,
            name: file.name,
          }
          this.sendVideoAttachment('', file, null, params)
        }, false);

        return
      }

      // Get image dimensions and size, optionally scale it down.
      imageScaled(file, maxImageDim, maxImageDim, maxExternAttachmentSize, false)
        .then(scaled => {

          const imagePreview = {
            url: URL.createObjectURL(scaled.blob),
            blob: scaled.blob,
            filename: scaled.name,
            width: scaled.width,
            height: scaled.height,
            size: scaled.blob.size,
            mime: scaled.mime
          }
          this.sendImageAttachment(null, scaled.blob, imagePreview)
        }).catch(err => {
          this.props.onError(err.message, 'err');
        });
    })
  }

  // handleFileDrop is called when the user drags & drops a file upon the message view.
  handleFileDrop(files) {
    if (!files || files.length == 0) {
      return;
    }
    // const file = files[0];
    const listImgFile = []
    const listFile = []
    const maxExternAttachmentSize = this.props.tinode.getServerParam('maxFileUploadSize', MAX_EXTERN_ATTACHMENT_SIZE);
    Array.prototype.forEach.call(files, (file) => {
      if (file && file.size <= maxExternAttachmentSize) {
        if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
          listImgFile.push(file)
        } else {
          listFile.push(file)
        }
      } else {
        Helpers.showErrorToasts(this.props.intl.formatMessage(messages.file_attachment_too_large,
          { size: bytesToHumanSize(file.size), limit: bytesToHumanSize(maxExternAttachmentSize) }))
      }
    })

    if (listImgFile.length > 0) {
      this.handleAttachImageOrVideo(listImgFile);
    }
    if (listFile.length > 0) {
      this.handleAttachFile(listFile);
    }
  }

  // sendAudioAttachment sends audio bits inband as Drafty message (no preview).
  sendAudioAttachment(url, preview, duration) {
    fetch(url)
      .then(result => result.blob())
      .then(blob => {
        // Server-provided limit reduced for base64 encoding and overhead.
        const maxInbandAttachmentSize = this.props.tinode.getServerParam('maxMessageSize', MAX_INBAND_ATTACHMENT_SIZE) * 0.75 - 1024;
        if (blob.size > maxInbandAttachmentSize) {
          // Too large to send inband - uploading out of band and sending as a link.
          const uploader = this.props.tinode.getLargeFileHelper();
          if (!uploader) {
            this.props.onError(this.props.intl.formatMessage(messages.cannot_initiate_upload));
            return;
          }
          const uploadCompletionPromise = uploader.upload(blob);
          const msg = Drafty.appendAudio(null, {
            mime: blob.type,
            size: blob.size,
            duration: duration,
            preview: preview,
            urlPromise: uploadCompletionPromise
          });
          // Pass data and the uploader to the TinodeWeb.
          this.sendMessage(msg, uploadCompletionPromise, uploader);
        } else {
          // Small enough to send inband.
          blobToBase64(blob)
            .then(b64 => {
              this.sendMessage(Drafty.appendAudio(null, {
                mime: b64.mime,
                bits: b64.bits,
                size: blob.size,
                duration: duration,
                preview: preview,
              }))
            })
        }
      })
      .catch(err => { this.props.onError(err.message, 'err') });;
  }

  handleCancelUpload(seq, uploader) {
    const topic = this.props.tinode.getTopic(this.state.topic);
    const found = topic.findMessage(seq);
    if (found) {
      found._cancelled = true;
    }
    uploader.cancel();
  }

  // seq: seq ID of the source message
  // context: message content.
  // senderId: UID of the sender of the source message.
  // senderName: full name of the sender of the original message.
  handlePickReply(msg, seq, content, senderId, senderName) {
    if (!seq || !content) {
      this.setState({ reply: null });
      return;
    }

    content = typeof content == 'string' ? Drafty.init(content) : content;
    if (Drafty.isValid(content)) {
      content = Drafty.replyContent(content, QUOTED_REPLY_LENGTH);
    } else {
      // /!\ invalid content.
      content = Drafty.append(Drafty.init('\u26A0 '),
        Drafty.wrapInto(this.props.intl.formatMessage(messages.invalid_content), 'EM'));
    }

    const reply = {
      content: Drafty.quote(senderName, senderId, content),
      seq: seq,
      tags: msg.tags,
      tagAll: msg.tagAll
    }

    this.setState({
      reply,
    });
    this.props.onCancelForwardMessage();

    this.props.tinode.messageEditArea.focus()
  }

  // seq: seq ID of the message to edit.
  // context: message content.
  handleEditMessage(seq, content) {
    if (!seq || !content) {
      this.setState({ reply: null });
      return;
    }

    content = typeof content == 'string' ? Drafty.init(content) : content;
    const editable = Drafty.toMarkdown(content);
    if (Drafty.isValid(content)) {
      content = Drafty.replyContent(content, EDIT_PREVIEW_LENGTH);
    } else {
      // /!\ invalid content.
      content = Drafty.append(Drafty.init('\u26A0 '),
        Drafty.wrapInto(this.props.intl.formatMessage(messages.invalid_content), 'EM'));
    }

    this.setState({
      reply: {
        content: Drafty.quote(this.props.intl.formatMessage(messages.editing_message), null, content),
        seq: seq,
        editing: true
      },
      contentToEdit: editable
    });
    this.props.onCancelForwardMessage();
  }

  // seq: seq ID of the message to edit.
  handleSaveMessage(msg) {
    this.props.tinode._db.updSavedMessage({
      savedTime: new Date().getTime(),
      topic: msg.topic,
      content: msg.content,
    })

    // SavedMessageServices.savedMessage(msg).catch((error) => {
    //   Helpers.showErrorToasts(this.props.intl.formatMessage({
    //     id: "notify_error",
    //     defaultMessage: "An error occurred. Please try again later",
    //   }))
    // });
  }



  handleCancelReply() {
    this.setState({ reply: null, contentToEdit: null });
    this.props.onCancelForwardMessage();
  }

  handleQuoteClick(replyToSeq, flash = true) {
    const ref = this.getOrCreateMessageRef(replyToSeq);
    if (ref && ref.current) {
      ref.current.scrollIntoView({ block: "center", behavior: "smooth" });
      if (flash) {
        ref.current.classList.add('flash');
        setTimeout(_ => {
          try {
            ref.current.classList.remove('flash');
          } catch (error) {

          }
        }, 1500);
      }
    }
  }

  isDragEnabled() {
    return this.state.isWriter && !this.state.unconfirmed && !this.props.forwardMessage && !this.state.peerMessagingDisabled;
  }

  handleDragStart(e) {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.clearData();
  }

  handleDragIn(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dragCounter++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      this.setState({ dragging: true });
    }
  }

  handleDragOut(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.setState({ dragging: false });
    }
  }

  handleDrag(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ dragging: false });
    if (this.isDragEnabled() && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      this.handleFileDrop(e.dataTransfer.files);
      this.dragCounter = 0;
    }
  }

  setMessagePhoneNotInstallApp(topicName) {
    const topicInfo = this.props.tinode.getTopic(topicName);
    let isOfficial = false;
    if (topicInfo && topicInfo.isOfficial) {
      isOfficial = topicInfo.isOfficial;
    }
    const phoneNumber = Topic.getThreadId(topicName);
    const formatedNumber = PhoneUtils.formatPhoneNumber(phoneNumber)
    const { formatMessage } = this.props.intl;
    if (isOfficial) {
      return formatMessage(messages.officialAccount_label)
    } else {
      if (formatedNumber && PhoneUtils.isFormatUnitelNumberLao(formatedNumber)) {
        return formatMessage(messages.internal_network_number)
      } else {
        return formatMessage(messages.out_of_network_number)
      }
    }
  }

  getLastSeen(cont) {
    const { formatMessage } = this.props.intl;
    if (cont) {
      if (cont?.online) {
        return formatMessage(messages.online_now)
      } else if (cont?.seen && cont?.seen?.when) {
        const lastSeen = cont.seen.when
        const timeSeen = Date.now() - lastSeen
        if (timeSeen < MAX_JUST_NOW) {
          return formatMessage(messages.justNow)
        } else if (timeSeen <= MAX_MINUTES_AGO) {
          const minutesAgo = Math.floor(timeSeen / 60000)
          const timeString = formatMessage(messages.minutesAgo, { minutes: minutesAgo })
          return formatMessage(messages.lastOnline, { timeString })
        } else if (timeSeen > MAX_MINUTES_AGO) {
          if (TimeHelpers.isCurrentYear(lastSeen)) {
            if (TimeHelpers.isCurrentDate(lastSeen)) {
              const timeString = TimeHelpers.getHHmm24(lastSeen)
              return formatMessage(messages.lastOnline, { timeString })
            } else if (TimeHelpers.isPreviouDate(lastSeen)) {
              const timeString = formatMessage(messages.lastSeenYesterday, { HHmm: TimeHelpers.getHHmm24(lastSeen) })
              return formatMessage(messages.lastOnline, { timeString })
            }
            return formatMessage(messages.lastOnline, { timeString: TimeHelpers.getDDMM(lastSeen) })
          }
          return formatMessage(messages.lastOnline, { timeString: TimeHelpers.getDDMMYYYY(lastSeen) })
        }
      } else {
        return null
      }
    }
    return null
  }

  render() {
    const { formatMessage } = this.props.intl;

    let component;
    if (!this.state.topic) {
      component = (
        <LogoView
          serverVersion={this.props.serverVersion}
          serverAddress={this.props.serverAddress} />
      );
    } else {
      let component2;
      if (this.state.imagePreview) {
        // Preview image before sending.
        component2 = (
          <ImagePreview
            content={this.state.imagePreview}
            tinode={this.props.tinode}
            reply={this.state.reply}
            onCancelReply={this.handleCancelReply}
            onClose={this.handleClosePreview}
            onSendMessage={this.sendImageAttachment} />
        );
      } else if (this.state.showPopup) {
        component2 = (
          <YetAnotherLightbox
            showModalImage={this.state.showPopup}
            listImagesMessage={this.state.listImagesShow}
            indexImage={this.state.indexImage}
            setStateIndexImage={(index, showPopup) => this.setState({ indexImage: index, showPopup })}
          />
          // <ImagePreview
          //   content={this.state.imagePreview}
          //   tinode={this.props.tinode}
          //   reply={this.state.reply}
          //   onCancelReply={this.handleCancelReply}
          //   onClose={this.handleClosePreview}
          //   onSendMessage={this.sendImageAttachment} />
        );
      }
      else if (this.state.videoPreview) {
        // Preview video.
        component2 = (
          <VideoPreview
            content={this.state.videoPreview}
            tinode={this.props.tinode}
            reply={this.state.reply}
            onShowAlert={this.props.onShowAlert}
            onError={this.props.onError}
            onCancelReply={this.handleCancelReply}
            onClose={this.handleClosePreview}
            onSendMessage={this.sendVideoAttachment} />
        );
      } else if (this.state.imagePostview) {
        // Expand received image.
        component2 = (
          <ImagePreview
            content={this.state.imagePostview}
            onClose={this.handleClosePreview} />
        );
      } else if (this.state.videoPostview) {
        // Play received video.
        component2 = (
          <VideoPreview
            topic={this.state.topic}
            content={this.state.videoPostview}
            tinode={this.props.tinode}
            onShowAlert={this.props.onShowAlert}
            onError={this.props.onError}
            onClose={this.handleClosePreview} />
        );
      } else if (this.state.docPreview) {
        // Preview attachment before sending.
        component2 = (
          <DocPreview
            content={this.state.docPreview}
            tinode={this.props.tinode}
            reply={this.state.reply}
            onCancelReply={this.handleCancelReply}
            onClose={this.handleClosePreview}
            onSendMessage={this.sendFileAttachment} />
        );
      } else if (this.state.rtcPanel) {
        component2 = (
          <CallPanel
            topic={this.state.topic}
            seq={this.props.callSeq}
            callState={this.props.callState}
            callAudioOnly={this.props.callAudioOnly}
            tinode={this.props.tinode}
            title={this.state.title}
            avatar={this.state.avatar || true}

            onError={this.props.onError}
            onHangup={this.handleCallHangup}
            onInvite={this.props.onCallInvite}
            onSendOffer={this.props.onCallSendOffer}
            onIceCandidate={this.props.onCallIceCandidate}
            onSendAnswer={this.props.onCallSendAnswer} />
        );
      } else {
        const topic = this.props.tinode.getTopic(this.state.topic);
        const isChannel = topic.isChannelType();
        const groupTopic = topic.isGroupType() && !isChannel;
        const icon_badges = [];
        if (topic.trusted) {
          if (topic.trusted.verified) {
            icon_badges.push({ icon: 'verified', color: 'badge-inv' });
          }
          if (topic.trusted.staff) {
            icon_badges.push({ icon: 'staff', color: 'badge-inv' });
          }
          if (topic.trusted.danger) {
            icon_badges.push({ icon: 'dangerous', color: 'badge-inv' });
          }
        }

        const messageNodes = [];
        let previousFrom = null;
        let prevDate = null;
        let chatBoxClass = null;
        topic.messages((msg, prev, next, i) => {
          let nextFrom = next ? (next.from || 'chan') : null;

          let sequence = 'single';
          let thisFrom = msg.from || 'chan';
          if (thisFrom == previousFrom && (new Date(msg.ts)).toDateString() === prevDate.toDateString()) {
            if (thisFrom == nextFrom) {
              sequence = 'middle';
            } else {
              sequence = 'last';
            }
          } else if (thisFrom == nextFrom) {
            sequence = 'first';
          }
          previousFrom = thisFrom;

          const isNotify = msg.subtype === MC_CONST.NODE_SUBTYPE_MESSAGE.NOTIFY
          const isVote = msg.subtype === MC_CONST.NODE_SUBTYPE_MESSAGE.VOTE;

          const isReply = !(thisFrom == this.props.myUserId);
          const deliveryStatus = topic.msgStatus(msg, true);

          let userFrom = thisFrom, userName, userAvatar;
          let topicInfo = this.props.tinode.getTopicByName(thisFrom, { isCreateWhenNotExist: true });
          if (topicInfo) {
            userName = topicInfo.getTopicName();
            userAvatar = makeImageUrl(topicInfo.public.photo);
          }
          chatBoxClass = groupTopic ? 'chat-box group' : 'chat-box';

          // Ref for this chat message.
          const ref = this.getOrCreateMessageRef(msg.seq);
          let replyToSeq = msg.head ? parseInt(msg.head.reply) : null;
          if (!replyToSeq || isNaN(replyToSeq)) {
            replyToSeq = null;
          }

          if (msg.hi) {
            // Deleted message.
            messageNodes.push(
              <MetaMessage
                deleted={true}
                key={msg.seq} />
            );
          } else {
            const thisDate = new Date(msg.ts);
            // This message was sent on a different date than the previous.
            if (!prevDate || prevDate.toDateString() != thisDate.toDateString()) {
              messageNodes.push(
                <MetaMessage
                  date={relativeDateFormat(msg.ts)}
                  locale={this.props.intl.locale}
                  key={'date-' + msg.seq} />
              );
              prevDate = thisDate;
            }
            const isOwnMessages = (thisFrom == this.props.myUserId);
            let myMotion = msg?.motions?.find(v => v.member === profile.username);
            let pollMsg = {};
            if (isVote) {
              pollMsg = topic.findPollMessageById(msg.id)
            }
            messageNodes.push(
              <ChatMessage
                isOfficial={topic && topic.isOfficial ? topic.isOfficial : false}
                msg={msg}
                pollMsg={pollMsg}
                tinode={this.props.tinode}
                content={msg.content}
                tags={msg.tags}
                tagAll={msg.tagAll}
                topic={this.state.topic}
                previewLink={msg.previewLink}
                onTopicSelected={this.props.onTopicSelected}
                mimeType={msg.head && msg.head.mime}
                myMotionId={myMotion}
                motions={msg.motions}
                replyToSeq={replyToSeq}
                edited={msg.head && !msg.head.webrtc && msg.head.replace}
                timestamp={msg.ts}
                response={isReply}
                isOwnMessages={isOwnMessages}
                seq={msg.seq}
                isGroup={groupTopic}
                isChan={this.state.channel}
                isNotify={isNotify}
                isVote={isVote}
                userFrom={userFrom}
                userName={userName}
                userAvatar={userAvatar}
                sequence={sequence}
                received={deliveryStatus}
                uploader={msg._uploader}
                userIsWriter={this.state.isWriter}
                viewportWidth={this.props.viewportWidth}  // Used by `formatter`.
                showContextMenu={this.handleShowMessageContextMenu}
                onExpandMedia={this.handleExpandMedia}
                onFormResponse={this.handleFormResponse}
                onCancelUpload={this.handleCancelUpload}
                pickReply={this.handlePickReply}
                editMessage={this.handleEditMessage}
                saveMessage={this.handleSaveMessage}
                onQuoteClick={this.handleQuoteClick}
                onError={this.props.onError}
                onCreateTopic={this.props.onCreateTopic}
                onShowDialog={this.props.onShowDialog}
                ref={ref}
                key={msg.seq} />
            );
          }
        });

        let lastSeen = null;
        if (isChannel) {
          lastSeen = formatMessage(messages.channel);
        } else {
          const cont = this.props.tinode.getMeTopic().getContact(this.state.topic);
          if (cont) {
            if (Tinode.isP2PTopicName(cont.topic)) {
              lastSeen = this.getLastSeen(cont);
            } else if (groupTopic) {
              const memberCount = topic && topic.sub && Array.isArray(topic.sub) ? topic.sub.filter(c => !c.deleted).length : 0
              lastSeen = this.props.intl.formatMessage(messages.member_count, { size: memberCount })
            }
          }

        }
        const avatar = this.state.avatar || true;
        const online = this.state.deleted ? null :
          this.props.online ? 'online' + (this.state.typingIndicator ? ' typing' : '') : 'offline';

        const titleClass = 'panel-title' + (this.state.deleted ? ' deleted' : '');
        let messagesComponent = (
          <>
            {topic && topic.pinMessage && <Pin
              topic={topic}
              tinode={this.props.tinode}
              onShowAlert={this.props.onShowAlert}
              handleQuoteClick={this.handleQuoteClick} />}
            <div id="messages-container">
              <button className={'action-button' + (this.state.showGoToLastButton ? '' : ' hidden')}
                onClick={this.goToLatestMessage}>
                <IconArrowDownward />
              </button>
              <div id="messages-panel"
                ref={this.handleScrollReference}
                style={{ backgroundImage: `url(${Helpers.getBackgroundSupport(topic.brgId)})` }}>
                {this.state.loaderMessage && <div className="loader-message"></div>}
                <ul id="scroller" className={chatBoxClass}>
                  {messageNodes}
                </ul>
              </div>
              {!this.state.isReader ?
                <div id="write-only-background">
                  {this.state.readingBlocked ?
                    <div id="write-only-note">
                      <FormattedMessage id="messages_not_readable" defaultMessage="no access to messages"
                        description="Message shown in topic without the read access" />
                    </div>
                    : null}
                </div>
                : null}
              {groupTopic &&
                <VoteNotification
                  tinode={this.props.tinode}
                  topic={topic}
                  onQuoteClick={this.handleQuoteClick}
                />}
              {this.state.typingIndicator &&
                <div class="typing">
                  <span className="username">
                    {this.state.title}
                  </span> <FormattedMessage
                    id="typing"
                    defaultMessage={"typing"}
                  />
                  <span class="jumping-dots">
                    <span class="dot-1"></span>
                    <span class="dot-2"></span>
                    <span class="dot-3"></span>
                  </span>
                </div>
              }
            </div>
            {this.state.peerMessagingDisabled && !this.state.unconfirmed ?
              <div id="peer-messaging-disabled-note">
                <i className="material-icons secondary">block</i> <FormattedMessage
                  id="peers_messaging_disabled" defaultMessage="Peer's messaging is disabled."
                  description="Shown when the p2p peer's messaging is disabled" /> <a href="#"
                    onClick={this.handleEnablePeer}><FormattedMessage id="enable_peers_messaging"
                      defaultMessage="Enable" description="Call to action to enable peer's messaging" /></a>.
              </div> : null}
            {this.state.unconfirmed ?
              <Invitation onAction={this.handleNewChatAcceptance} />
              :
              <SendMessage
                tinode={this.props.tinode}
                topic={topic}
                topicName={this.state.topic}
                noInput={false}
                disabled={!this.state.isWriter || this.state.deleted}
                reply={this.state.reply}
                initMessage={this.state.contentToEdit}
                onKeyPress={this.sendKeyPress}
                onRecordingProgress={this.sendKeyPress}
                onSendMessage={this.sendMessage}
                onAttachFile={this.props.forwardMessage ? null : this.handleAttachFile}
                onAttachImage={this.props.forwardMessage ? null : this.handleAttachImageOrVideo}
                onAttachAudio={this.props.forwardMessage ? null : this.sendAudioAttachment}
                onError={this.props.onError}
                onQuoteClick={this.handleQuoteClick}
                onCancelReply={this.handleCancelReply}
                contacts={this.props.contacts}
                groupMembers={this.state.groupMembers}
                myUserId={this.props.myUserId}
                isGroup={groupTopic}
                onShowDialog={this.props.onShowDialog}
                typingIndicator={this.state.typingIndicator}
                title={this.state.title}
              />}
          </>
        );

        component2 = (
          <>
            <div id="topic-caption-panel" className="caption-panel">
              {this.props.displayMobile ?
                <a href="#" id="hide-message-view" onClick={(e) => { e.preventDefault(); this.leave(this.state.topic); this.props.onHideMessagesView(); }}>
                  <i className="material-icons">arrow_back</i>
                </a>
                :
                null}

              {groupTopic ?
                <div
                  className="avatar-box"
                  onClick={this.handleContextClick}
                >
                  <LetterTile
                    tinode={this.props.tinode}
                    avatar={avatar}
                    topic={this.state.topic}
                    title={this.state.title}
                    deleted={this.state.deleted}
                  />
                  {/* {!isChannel ? <span className={online} /> : null} */}
                </div> :
                <div
                  className="avatar-box"
                  onClick={() => this.props.onShowDialog(Tinode.DIALOG_TYPE.PERSONAL_INFORMATION, { topicProfile: this.state.topic })}
                >
                  <LetterTile
                    tinode={this.props.tinode}
                    avatar={avatar}
                    topic={this.state.topic}
                    title={this.state.title}
                    deleted={this.state.deleted}
                  />
                  {/* {!isChannel ? <span className={online} /> : null} */}
                </div>
              }

              <div id="topic-title-group">
                <div id="topic-title" className={titleClass}>{
                  this.state.title ||
                  <i><FormattedMessage id="unnamed_topic" defaultMessage="Unnamed"
                    description="Title shown when the topic has no name" /></i>
                }<ContactBadges badges={icon_badges} /></div>
                <div id="topic-last-seen">{lastSeen ? lastSeen : this.setMessagePhoneNotInstallApp(this.state.topic)}</div>
              </div>
              {groupTopic ?
                <GroupSubs
                  tinode={this.props.tinode}
                  subscribers={this.state.onlineSubs}
                  topic={this.props.topic}
                  groupMembers={this.state.groupMembers}
                  handleShowGroupMembers={this.props.handleShowGroupMembers}
                /> :
                <div id="topic-users" />
              }
              <div className="topic-more-vert tooltip-container">
                <a href="#" onClick={this.handleContextClick}>
                  <IconMoreVert />
                </a>
                <div className="tooltip tooltip-left">
                  <FormattedMessage
                    id="panel_title_info"
                    defaultMessage="Info"
                  />
                </div>
              </div>
            </div>
            {this.props.displayMobile ?
              <ErrorPanel
                level={this.props.errorLevel}
                text={this.props.errorText}
                onClearError={this.props.onError} />
              : null}
            {/* <LoadSpinner show={this.state.fetchingMessages} /> */}
            {messagesComponent}
            {this.state.dragging && this.isDragEnabled() ?
              <div className="drag-n-drop">{formatMessage(messages.drag_file)}</div>
              : null}
          </>
        );
      }
      component = <div id="topic-view" ref={this.mountDnDEvents}>{component2}</div>
    }
    return component;
  }
};

export default injectIntl(MessagesView);
